<template>
  <render />
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  onBeforeUnmount,
  type VNode,
  ref,
  type CSSProperties,
} from 'vue'
import cuid from '@paralleldrive/cuid2'
import { find } from 'lodash-es'
import { useCurrentElement } from '@vueuse/core'
import { type PackingContainerSlotProp } from './PmPackingContainerPure.vue'

export type Props = {
  packingContainer: PackingContainerSlotProp
  /** Pass an id for debugging purposes, must be defined when the component is created */
  id?: string
  isInViewport?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isInViewport: undefined,
})

export type Slots = {
  default: (slotProps: { packedElement: typeof packedElement.value }) => VNode[]
}

const slots = defineSlots<Slots>()

const elRoot = useCurrentElement()
const id = props.id ?? cuid.createId()

const packedElement = computed(() => {
  return find(props.packingContainer.packedElements, {
    id,
  })
})

const stylesPerformance = computed(() => {
  if (props.packingContainer.contentCanBeHidden !== true) return

  const result = {
    // contentVisibility: 'hidden',
    containIntrinsicWidth: `${dimensions.value.width}px`,
    containIntrinsicHeight: `${dimensions.value.height}px`,
    width: `${dimensions.value.width}px`,
    height: `${dimensions.value.height}px`,
  } satisfies CSSProperties

  return result
})

const styles = computed(() => {
  const top = packedElement.value?.y

  return {
    ...stylesPerformance.value,
    top: top ? `${top}px` : undefined,
  }
})

function assertElRoot(el: typeof elRoot.value): asserts el is HTMLElement {
  if (!elRoot.value) {
    throw new Error('elRoot is undefined')
  }
  if (!(elRoot.value instanceof HTMLElement)) {
    throw new Error('elRoot is not a HTMLELement')
  }
}

async function requestSlot() {
  assertElRoot(elRoot.value)
  await new Promise((resolve) => setTimeout(resolve, 0))

  props.packingContainer.addElement({
    ...getDimensions(),
    id: id,
    updateDimensions: updateDimensions,
    element: elRoot.value,
  })
}

function unrequestSlot() {
  props.packingContainer.removeElement({
    id: id,
  })
}

export type Dimensions = { width?: number; height?: number }
const dimensions = ref<Dimensions>({})

function getDimensions() {
  assertElRoot(elRoot.value)
  const rect = elRoot.value.getBoundingClientRect()

  const offsetParent = elRoot.value.offsetParent
  const offset = (offsetParent && offsetParent.getBoundingClientRect().x) ?? 0

  dimensions.value = {
    width: rect.width,
    height: rect.height,
  }

  const result = {
    x: rect.x - offset,
    width: rect.width,
    height: rect.height,
  }

  return result
}

function updateDimensions() {
  if (props.isInViewport === false) {
    // console.log('dont update dimensions because not in viewport')
    return
  }

  props.packingContainer.updateDimensionsOfElement({
    ...getDimensions(),
    id: id,
  })
}

onMounted(() => {
  requestSlot()

  // Re-pack if webfonts where loaded (possible change in dimensions!)
  document.fonts.ready.then(updateDimensions)
})

onBeforeUnmount(unrequestSlot)

const render = () => {
  return slots.default({
    packedElement: packedElement.value,
  })[0]
}
</script>
